import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "../users/userform.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiArrowLeft } from "react-icons/fi";
import InternalFooter from "../components/InternalFooter/InternalFooter";
import { createStaffAddress, updateStaffAddress, getStaffById } from "../../services/StaffService";

const StaffAddressForm = () => {
  const navigate = useNavigate();
  const { staffId, addressId } = useParams();
  const location = useLocation();
  const isEditMode = !!addressId;

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [dataLoaded, setDataLoaded] = useState(!isEditMode);

  const [formData, setFormData] = useState({
    address_type: "Present",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    is_current: false
  });

  const notifySuccess = (msg) => toast.success(`✔️ ${msg}`, { position: 'top-right', autoClose: 3000 });
  const notifyError = (msg) => toast.error(`❌ ${msg}`, { position: 'top-right', autoClose: 5000 });

  useEffect(() => {
    if (isEditMode) {
      fetchAddressData();
    }
  }, [staffId, addressId]);

  const fetchAddressData = async () => {
    try {
      setLoading(true);
      const res = await getStaffById(staffId);
      const staff = res.data.data || res.data;
      const address = staff.StaffAddresses?.find(addr => addr.id === parseInt(addressId));
      
      if (address) {
        setFormData({
          address_type: address.address_type || "Present",
          address_line_1: address.address_line_1 || "",
          address_line_2: address.address_line_2 || "",
          city: address.city || "",
          state: address.state || "",
          country: address.country || "",
          postal_code: address.postal_code || "",
          is_current: address.is_current || false
        });
      } else {
        notifyError("Address not found");
        navigate(`/staff/staffDetails/${staffId}`);
      }
      setDataLoaded(true);
    } catch (error) {
      console.error("Address fetch error:", error);
      notifyError(error?.response?.data?.message || "Failed to load address data");
      setDataLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const mustIncludeLetters = (str) => {
    const trimmed = (str || '').trim();
    if (!trimmed) return true;
    return /[a-zA-Z]/.test(trimmed);
  };

  const isOnlyDigits = (str) => {
    const trimmed = (str || '').trim();
    if (!trimmed) return true; // empty is ok for validation (required checked separately)
    return /^\d+$/.test(trimmed);
  };

  const validate = () => {
    const errs = {};
    if (!formData.address_line_1.trim()) errs.address_line_1 = "Address line 1 is required";
    else if (!mustIncludeLetters(formData.address_line_1)) errs.address_line_1 = "Address line 1 must include letters and cannot be only numbers or special characters.";
    if (formData.address_line_2.trim() && !mustIncludeLetters(formData.address_line_2)) errs.address_line_2 = "Address line 2 must include letters and cannot be only numbers or special characters.";
    if (!formData.city.trim()) errs.city = "City is required";
    else if (!mustIncludeLetters(formData.city)) errs.city = "City must include letters and cannot be only numbers or special characters.";
    if (!formData.state.trim()) errs.state = "State is required";
    else if (!mustIncludeLetters(formData.state)) errs.state = "State must include letters and cannot be only numbers or special characters.";
    if (!formData.country.trim()) errs.country = "Country is required";
    else if (!mustIncludeLetters(formData.country)) errs.country = "Country must include letters and cannot be only numbers or special characters.";
    if (!formData.postal_code.trim()) errs.postal_code = "Postal code is required";
    else if (!isOnlyDigits(formData.postal_code)) errs.postal_code = "Postal code must contain only numbers.";
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      notifyError("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    try {
      if (isEditMode) {
        await updateStaffAddress(staffId, addressId, formData);
        notifySuccess("Address updated successfully");
      } else {
        await createStaffAddress(staffId, formData);
        notifySuccess("Address added successfully");
      }
      setTimeout(() => navigate(`/staff/staffDetails/${staffId}`), 1500);
    } catch (error) {
      console.error("Submit error:", error);
      notifyError(error?.response?.data?.message || error?.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'add'} address`);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  if (!dataLoaded && loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        className="back-button"
        onClick={() => navigate(`/staff/staffDetails/${staffId}`)}
      >
        <FiArrowLeft style={{ marginRight: "0.5rem" }} />
        Back
      </button>

      <ToastContainer />

      <div className="card p-3 p-md-4 mt-2">
        <h4 className="mb-4">{isEditMode ? "Edit Address" : "Add Address"}</h4>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-12 col-md-6 mb-3">
              <label>Address Type*</label>
              <select
                name="address_type"
                value={formData.address_type}
                onChange={handleChange}
                className={`form-control ${errors.address_type ? 'is-invalid' : ''}`}
                required
              >
                <option value="Present">Present</option>
                <option value="Permanent">Permanent</option>
              </select>
              {errors.address_type && <div className="text-danger small">{errors.address_type}</div>}
            </div>

            <div className="col-md-6 mb-3 d-flex align-items-end">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="is_current"
                  id="is_current"
                  checked={formData.is_current}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="is_current">
                  Set as Current Address
                </label>
              </div>
            </div>

            <div className="col-12 mb-3">
              <label>Address Line 1*</label>
              <input
                name="address_line_1"
                value={formData.address_line_1}
                onChange={handleChange}
                className={`form-control ${errors.address_line_1 ? 'is-invalid' : ''}`}
                placeholder="Enter address line 1"
                required
              />
              {errors.address_line_1 && <div className="text-danger small">{errors.address_line_1}</div>}
            </div>

            <div className="col-12 mb-3">
              <label>Address Line 2</label>
              <input
                name="address_line_2"
                value={formData.address_line_2}
                onChange={handleChange}
                className={`form-control ${errors.address_line_2 ? 'is-invalid' : ''}`}
                placeholder="Enter address line 2 (optional)"
              />
              {errors.address_line_2 && <div className="text-danger small">{errors.address_line_2}</div>}
            </div>

            <div className="col-12 col-md-4 mb-3">
              <label>City*</label>
              <input
                name="city"
                value={formData.city}
                onChange={handleChange}
                className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                placeholder="Enter city"
                required
              />
              {errors.city && <div className="text-danger small">{errors.city}</div>}
            </div>

            <div className="col-12 col-md-4 mb-3">
              <label>State*</label>
              <input
                name="state"
                value={formData.state}
                onChange={handleChange}
                className={`form-control ${errors.state ? 'is-invalid' : ''}`}
                placeholder="Enter state"
                required
              />
              {errors.state && <div className="text-danger small">{errors.state}</div>}
            </div>

            <div className="col-12 col-md-4 mb-3">
              <label>Country*</label>
              <input
                name="country"
                value={formData.country}
                onChange={handleChange}
                className={`form-control ${errors.country ? 'is-invalid' : ''}`}
                placeholder="Enter country"
                required
              />
              {errors.country && <div className="text-danger small">{errors.country}</div>}
            </div>

            <div className="col-12 col-md-4 mb-3">
              <label>Postal Code*</label>
              <input
                name="postal_code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={formData.postal_code}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || /^\d+$/.test(v)) {
                    setFormData((prev) => ({ ...prev, postal_code: v }));
                    if (errors.postal_code) setErrors((prev) => { const n = { ...prev }; delete n.postal_code; return n; });
                  }
                }}
                className={`form-control ${errors.postal_code ? 'is-invalid' : ''}`}
                placeholder="Enter postal code (numbers only)"
                required
              />
              {errors.postal_code && <div className="text-danger small">{errors.postal_code}</div>}
            </div>
          </div>

          <div className="toolbar toolbar-bottom p-2">
            <div className="d-flex flex-row justify-content-end gap-2 w-100">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={() => navigate(`/staff/staffDetails/${staffId}`)}
                disabled={isSubmitting}
                style={{ flex: 1, maxWidth: '200px', minWidth: '100px' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn user-form-btn sw-btn-next"
                style={{ flex: 1, maxWidth: '200px', minWidth: '100px' }}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                ) : null}
                {isEditMode ? "Update" : "Submit"}
              </button>
            </div>
          </div>
        </form>
      </div>

      <InternalFooter />
    </div>
  );
};

export default StaffAddressForm;
